import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { openDebitNoteReport } from '../../../utils/openInventoryReport';
import { canPrintDebitNote } from '../../../utils/documentPrintGate';
import { useAuth } from '../../../contexts/AuthContext';
import ProcurementDocuments from '../../Common/ProcurementDocuments';

const statusColors = {
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
  completed: 'bg-blue-100 text-blue-800'
};

const DEBIT_NOTE_TYPE_LABELS = {
  ADJUSTMENT: 'Price / quantity adjustment',
  PENALTY: 'Penalty / liquidated damages',
  REBATE: 'Rebate / discount'
};

const DebitNoteView = ({ debitNoteId, onClose }) => {
  const { hasPermission } = useAuth();
  const [debitNote, setDebitNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    load();
    return () => { document.body.style.overflow = 'unset'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debitNoteId]);

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[DebitNoteView] could not load location info:', e.message);
      }
    })();
  }, []);

  const load = async () => {
    try {
      const res = await inventoryApi.getDebitNoteById(debitNoteId);
      setDebitNote(res.data);
    } catch (err) {
      toast.error('Failed to load debit note');
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    await openDebitNoteReport(debitNote, companyInfo);
  };

  const fmt = (n) => parseFloat(n || 0).toFixed(2);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-3xl max-h-[92vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{debitNote?.debitNoteNumber || 'Debit Note'}</h3>
          <div className="flex items-center gap-2">
            {debitNote && canPrintDebitNote(debitNote.status) && (
              <button
                onClick={handlePrint}
                title="Print PDF"
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print PDF
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? <div className="p-10"><LoadingSpinner /></div> : debitNote && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Supplier</p>
                <p className="font-medium text-gray-900">{debitNote.supplierName} {debitNote.supplierCode ? `(${debitNote.supplierCode})` : ''}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[debitNote.status] || 'bg-gray-100 text-gray-800'}`}>
                {debitNote.status?.replace('_', ' ')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">Date:</span> {fmtDate(debitNote.debitDate)}</div>
              <div><span className="text-gray-500">GRNs:</span> {debitNote.grnNumbersDisplay || (debitNote.grns || []).map(g => g.grnNumber).join(', ') || '—'}</div>
              <div>
                <span className="text-gray-500">Type:</span>{' '}
                {debitNote.source === 'SUPPLIER_RETURN'
                  ? <>Supplier return <span className="font-medium">{debitNote.supplierReturnNumber}</span></>
                  : DEBIT_NOTE_TYPE_LABELS[debitNote.noteType] || DEBIT_NOTE_TYPE_LABELS.ADJUSTMENT}
              </div>
              {debitNote.relatedGrnNumber && <div><span className="text-gray-500">Related GRN:</span> {debitNote.relatedGrnNumber}</div>}
              {(debitNote.costCenterLabel || debitNote.costCenter) && <div><span className="text-gray-500">Cost center:</span> {debitNote.costCenterLabel || 'from the related GRN'}</div>}
              <div>
                <span className="text-gray-500">GL entry:</span>{' '}
                {debitNote.source === 'SUPPLIER_RETURN'
                  ? 'posted by the supplier return'
                  : debitNote.glReversal?.docCode
                    ? <>reversed ({debitNote.glReversal.docCode})</>
                    : <span className={debitNote.glTransaction?.status === 'posted' ? 'text-green-700' : debitNote.glTransaction?.status === 'failed' ? 'text-red-600' : ''}>
                        {debitNote.glTransaction?.status || (debitNote.status === 'pending_approval' ? 'on approval' : 'not posted')}
                        {debitNote.glTransaction?.message ? ` — ${debitNote.glTransaction.message}` : ''}
                      </span>}
              </div>
              {debitNote.cancellation?.cancelledAt && (
                <div className="col-span-2 text-red-700">
                  Cancelled {fmtDate(debitNote.cancellation.cancelledAt)}{debitNote.cancellation.reason ? ` — ${debitNote.cancellation.reason}` : ''}
                </div>
              )}
            </div>

            {/* Charge lines (penalty / rebate) */}
            {(debitNote.chargeLines || []).length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-xs text-gray-600">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Description</th>
                      <th className="px-3 py-1.5 text-left">Reference</th>
                      <th className="px-3 py-1.5 text-left">Credited To</th>
                      <th className="px-3 py-1.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debitNote.chargeLines.map((c, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">{c.description}</td>
                        <td className="px-3 py-1.5">{c.reference || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-600">{c.ledgerAccount?.label || '—'}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Line items */}
            {(debitNote.lineItems || []).length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-xs text-gray-600">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Item / Service</th>
                    <th className="px-3 py-1.5 text-left">GRN</th>
                    <th className="px-3 py-1.5 text-right">Qty</th>
                    <th className="px-3 py-1.5 text-right">Unit Price</th>
                    <th className="px-3 py-1.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(debitNote.lineItems || []).map((li, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-1.5">{li.itemName} <span className="text-xs text-gray-400">{li.itemCode}</span></td>
                      <td className="px-3 py-1.5">{li.sourceGrnNumber}</td>
                      <td className="px-3 py-1.5 text-right">{li.quantity}</td>
                      <td className="px-3 py-1.5 text-right">{fmt(li.unitPrice)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{fmt(li.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmt(debitNote.totals?.totalValue)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{fmt(debitNote.totals?.taxAmount)}</span></div>
                <div className="flex justify-between font-bold border-t border-gray-200 pt-1"><span>Total</span><span className="text-amber-700">{fmt(debitNote.totals?.totalWithTax)}</span></div>
              </div>
            </div>

            {/* Setoff status */}
            {debitNote.status === 'approved' || debitNote.status === 'completed' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-amber-800 mb-1">Payment Setoff</p>
                <div className="flex justify-between"><span className="text-gray-600">Set off so far</span><span>{fmt(debitNote.paymentSetoff?.setoffAmount)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Remaining</span><span>{fmt(debitNote.paymentSetoff?.remainingAmount)}</span></div>
              </div>
            ) : null}

            {/* GL posting status */}
            {debitNote.status === 'approved' || debitNote.status === 'completed' ? (
              <div className={`border rounded-lg p-3 text-sm ${
                debitNote.glTransaction?.status === 'posted' ? 'bg-green-50 border-green-200' :
                debitNote.glTransaction?.status === 'failed' ? 'bg-red-50 border-red-200' :
                debitNote.glTransaction?.status === 'skipped' ? 'bg-yellow-50 border-yellow-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <p className="font-semibold mb-1">GL Posting</p>
                <p>
                  Status: <span className="font-medium">{debitNote.glTransaction?.status || 'pending'}</span>
                </p>
                {debitNote.glTransaction?.transactionId && (
                  <p className="text-xs text-gray-500 mt-1">Transaction ID: {debitNote.glTransaction.transactionId}</p>
                )}
                {debitNote.glTransaction?.message && (
                  <p className="text-xs text-gray-700 mt-1">{debitNote.glTransaction.message}</p>
                )}
              </div>
            ) : null}

            {/* Supporting documents */}
            <ProcurementDocuments docType="debit-notes" docId={debitNote._id}
              canEdit={hasPermission('INVENTORY:DEBIT_NOTES:UPDATE') || hasPermission('INVENTORY:DEBIT_NOTES:CREATE')} />

            {debitNote.reasonDescription && (
              <div><p className="text-xs text-gray-500 mb-1">Reason</p><p className="text-sm text-gray-800">{debitNote.reasonDescription}</p></div>
            )}
            {debitNote.approvalInfo?.rejectionReason && (
              <div><p className="text-xs text-gray-500 mb-1">Rejection Reason</p><p className="text-sm text-red-700">{debitNote.approvalInfo.rejectionReason}</p></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DebitNoteView;
